import React, { useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";

const initial = {
  name: "", email: "", phone: "", company: "", role: "", industry: "", location: "",
  website: "", linkedin: "", years: "", company_size: "",
  what_built: "", lessons: "", challenges: "", topics: "", preferred_format: "written", notes: "",
};

const PROCESS = [
  { step: "01", title: "Pitch an idea or experience", body: "Submit a short application. Tell us what you've done and the operating lessons you'd bring." },
  { step: "02", title: "Editorial review", body: "We read every application personally. If your background fits our current editorial direction, we get in touch." },
  { step: "03", title: "Intro conversation", body: "A short call with the editor to shape the angle and confirm the format." },
  { step: "04", title: "Interview, draft, or collaboration", body: "You can write the piece yourself, be interviewed, or collaborate with editors. Whichever produces the strongest work." },
  { step: "05", title: "Editing and fact checking", body: "We edit for clarity and check the details. Nothing runs without your review." },
  { step: "06", title: "Publication", body: "Your piece goes live on Operating Class. You're credited, linked, and archived." },
  { step: "07", title: "Distribution and reusable assets", body: "Newsletter feature, social distribution, and reusable links and clips you can share on your own channels." },
];

const VALUE = [
  "Association with a thoughtful, professional business audience.",
  "Credible, edited publication of the ideas you've earned in practice.",
  "Editorial help shaping your experience into something useful to other operators.",
  "Backlinks and profile links to your site or company where appropriate.",
  "Social distribution and newsletter feature on publication.",
  "Reusable video, audio, or written clips you can share on your own channels.",
  "Authority around a subject you actually understand.",
];

export default function Contribute() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/applications", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done)
    return (
      <div data-testid="contribute-success" className="max-w-reading mx-auto px-6 py-32 text-center">
        <div className="eyebrow text-oc-coral mb-6">Received</div>
        <h1 className="font-editorial text-4xl md:text-5xl text-oc-ink leading-tight">
          Thank you. We have your pitch.
        </h1>
        <p className="mt-8 font-editorial text-lg text-oc-ink-soft leading-relaxed">
          The Operating Class editorial team reads every submission personally. If your background fits our current editorial direction, we&#39;ll be in touch. Because we run a small, curated publication, our response window is typically two to four weeks.
        </p>
        <p className="mt-6 font-editorial text-lg text-oc-ink-soft leading-relaxed">
          In the meantime, welcome. We hope you&#39;ll spend time in the archive.
        </p>
      </div>
    );

  return (
    <div data-testid="contribute-page">
      {/* HERO */}
      <section className="border-b border-oc-hairline">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="eyebrow text-oc-coral mb-6">Contribute</div>
          <h1 className="hero-headline text-5xl md:text-7xl text-oc-ink max-w-4xl">
            You bring the experience. We help turn it into something useful to other operators.
          </h1>
          <p className="mt-8 font-editorial text-xl md:text-2xl text-oc-ink-soft max-w-3xl leading-snug">
            Operating Class publishes lessons from founders, executives, operators, specialists, investors, and professionals with meaningful firsthand experience. The standard isn&#39;t fame. The standard is: have you actually done something worth teaching?
          </p>
        </div>
      </section>

      {/* PROCESS */}
      <section className="border-b border-oc-hairline bg-oc-sand">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="max-w-3xl mb-12 md:mb-16">
            <div className="eyebrow text-oc-coral mb-6">How it works</div>
            <h2 className="font-editorial text-3xl md:text-5xl leading-tight text-oc-ink">
              A slow, edited process. Nothing runs without your review.
            </h2>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p) => (
              <li key={p.step} className="p-6 md:p-7 bg-white border border-oc-hairline">
                <div className="font-ui font-bold text-oc-coral text-xs uppercase tracking-widest mb-3">{p.step}</div>
                <div className="font-editorial text-xl text-oc-ink leading-snug">{p.title}</div>
                <p className="mt-3 font-editorial text-sm text-oc-ink-soft leading-relaxed">
                  {p.body}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* VALUE + FORMATS + POLICY */}
      <section className="border-b border-oc-hairline">
        <div className="max-w-editorial mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-7">
              <div className="eyebrow text-oc-coral mb-6">What contributing gives you</div>
              <h3 className="font-editorial text-3xl md:text-4xl leading-tight text-oc-ink mb-8">
                Real value, honestly stated.
              </h3>
              <ul className="space-y-4">
                {VALUE.map((v, i) => (
                  <li
                    key={i}
                    className="flex gap-4 pl-0 border-b border-oc-hairline pb-4"
                  >
                    <span className="font-ui font-bold text-oc-coral text-xs mt-1.5 w-7 flex-shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-editorial text-lg text-oc-ink leading-snug">
                      {v}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="lg:col-span-5 space-y-10">
              <div>
                <div className="eyebrow text-oc-coral mb-4">Contribution formats</div>
                <ul className="space-y-3 font-editorial text-lg text-oc-ink-soft leading-snug">
                  <li>· Written pitch you author (with editorial support).</li>
                  <li>· Recorded interview with the editor (60&ndash;90 minutes).</li>
                  <li>· Framework, checklist, or template drawn from your practice.</li>
                  <li>· Collaborative co-write with editors.</li>
                </ul>
              </div>

              <div className="border-l-2 border-oc-blue pl-6 py-2">
                <div className="eyebrow text-oc-ink-muted mb-3">Payment &amp; commercial policy</div>
                <p className="font-editorial text-base text-oc-ink leading-relaxed">
                  Editorial contributions are <strong>unpaid by default</strong>. If a paid commission is agreed in advance, that is stated in writing before work begins. Operating Class does not charge legitimate editorial contributors for publication.
                </p>
                <p className="mt-4 font-editorial text-base text-oc-ink leading-relaxed">
                  Sponsored or commercial content is handled as a separate arrangement and clearly labeled on the site. Editorial independence and commercial relationships stay distinct.
                </p>
              </div>

              <div className="border-l-2 border-oc-coral pl-6 py-2">
                <div className="eyebrow text-oc-ink-muted mb-3">What we don&#39;t do</div>
                <ul className="space-y-2 font-editorial text-base text-oc-ink-soft">
                  <li>· Puff pieces.</li>
                  <li>· Unlabeled sponsored coverage.</li>
                  <li>· Gotcha interviews.</li>
                  <li>· AI-generated business advice.</li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section className="max-w-editorial mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="max-w-3xl mb-12">
          <div className="eyebrow text-oc-coral mb-6">Pitch</div>
          <h2 className="font-editorial text-3xl md:text-5xl leading-tight text-oc-ink">
            Tell us what you&#39;ve built.
          </h2>
          <p className="mt-4 font-editorial text-lg text-oc-ink-soft leading-relaxed">
            Every field with an asterisk is required. We read every submission personally. Response typically within two to four weeks.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-8 max-w-4xl" data-testid="contribute-form">
          <FieldGroup title="About you">
            <Field label="Name" required value={form.name} onChange={set("name")} testId="cb-name" />
            <Field label="Email" required type="email" value={form.email} onChange={set("email")} testId="cb-email" />
            <Field label="Phone" value={form.phone} onChange={set("phone")} testId="cb-phone" />
            <Field label="Location" value={form.location} onChange={set("location")} testId="cb-location" placeholder="City, state/country" />
          </FieldGroup>

          <FieldGroup title="Your company">
            <Field label="Company" required value={form.company} onChange={set("company")} testId="cb-company" />
            <Field label="Your role" required value={form.role} onChange={set("role")} testId="cb-role" />
            <Field label="Industry" value={form.industry} onChange={set("industry")} testId="cb-industry" />
            <Field label="Company size" value={form.company_size} onChange={set("company_size")} testId="cb-company-size" placeholder="e.g. 45 employees, $8M revenue" />
            <Field label="Website" value={form.website} onChange={set("website")} testId="cb-website" />
            <Field label="LinkedIn" value={form.linkedin} onChange={set("linkedin")} testId="cb-linkedin" />
            <Field label="Years in business / industry" value={form.years} onChange={set("years")} testId="cb-years" />
          </FieldGroup>

          <FieldGroup title="What you can teach">
            <Textarea label="What have you built or operated?" required value={form.what_built} onChange={set("what_built")} testId="cb-built" rows={5} />
            <Textarea label="What operational lessons are worth documenting?" required value={form.lessons} onChange={set("lessons")} testId="cb-lessons" rows={5} />
            <Textarea label="Major operating challenges you&#39;ve faced" value={form.challenges} onChange={set("challenges")} testId="cb-challenges" rows={4} />
            <Textarea label="Topics you can speak about with depth" value={form.topics} onChange={set("topics")} testId="cb-topics" rows={3} placeholder="e.g. Hiring, cash flow, succession, systems, delegation..." />
            <div className="col-span-full">
              <label className="eyebrow text-oc-ink-muted mb-3 block">Preferred format</label>
              <select
                data-testid="cb-format"
                value={form.preferred_format}
                onChange={set("preferred_format")}
                className="w-full h-12 px-4 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-base"
              >
                <option value="written">Written pitch (I&#39;ll author it)</option>
                <option value="audio">Recorded audio interview</option>
                <option value="video">Recorded video interview</option>
                <option value="framework">Framework, checklist, or template</option>
                <option value="any">Open to any format</option>
              </select>
            </div>
            <Textarea label="Anything else we should know?" value={form.notes} onChange={set("notes")} testId="cb-notes" rows={3} />
          </FieldGroup>

          <div className="pt-4 flex flex-wrap gap-4 items-center">
            <button
              data-testid="contribute-submit"
              type="submit"
              disabled={submitting}
              className="h-14 px-10 bg-oc-blue text-white font-ui text-sm font-bold uppercase tracking-widest hover:bg-oc-blue-hover disabled:opacity-60 transition-colors"
            >
              {submitting ? "Sending..." : "Submit pitch"}
            </button>
            <Link
              to="/nominate"
              className="inline-flex items-center gap-2 font-ui text-xs font-bold uppercase tracking-widest text-oc-ink hover:text-oc-blue"
            >
              Or nominate someone else <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <p className="font-editorial text-sm text-oc-ink-muted">
            We read every submission. Response typically within two to four weeks.
          </p>
        </form>
      </section>
    </div>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div className="pb-8 border-b border-oc-hairline">
      <div className="eyebrow text-oc-coral mb-6">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{children}</div>
    </div>
  );
}

function Field({ label, value, onChange, required, type = "text", placeholder, testId }) {
  return (
    <label className="block col-span-1">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}{required && " *"}</span>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full h-12 px-4 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-base"
      />
    </label>
  );
}

function Textarea({ label, value, onChange, required, rows = 4, placeholder, testId }) {
  return (
    <label className="block col-span-full">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}{required && " *"}</span>
      <textarea
        data-testid={testId}
        rows={rows}
        required={required}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full px-4 py-3 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-editorial text-base leading-relaxed resize-y"
      />
    </label>
  );
}
